/**
 * Image Detail Modal V2
 *
 * Modal zur Anzeige eines Bildes in Vollgröße mit Details und Aktionen.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  Download,
  Dices,
  Sparkles,
  Globe,
  Trash2,
  Calendar,
  Palette,
  Maximize2,
  Clock,
  Code,
  MessageSquare,
  Tag as TagIcon,
  BoxSelect,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { useImageActions } from '@/hooks/useImageActions';
import { useImageRating } from '@/hooks/useImageRating';
import { TagInput } from '@/components/v2/ui';
import { GalleryAddToProductBridge } from './GalleryAddToProductBridge';
import { AIMetadataSection } from './AIMetadataSection';
import { ImageRatingInput } from '@/components/v2/tools/gallery-v2/rating/ImageRatingInput';
import { getImageTags, addTagsToImage, removeTagFromImage, createTag, type Tag } from '@/lib/api/tags';
import { Button } from '@/components/v2/ui';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

export interface ImageDetailModalProps {
  image: Image | null;
  isOpen: boolean;
  onClose: () => void;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
}

export function ImageDetailModal({ image, isOpen, onClose, onNavigatePrev, onNavigateNext }: ImageDetailModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    deleteImage,
    publishImage,
    downloadImage,
    generateVariation,
    isDeleting,
    isPublishing,
    isDownloading,
    isGeneratingVariation,
  } = useImageActions();

  // Rating Hook
  const { mutate: rateImage, isPending: isRatingPending } = useImageRating(image?.id || null);

  // Tags State
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Transfer Dialog State
  const [showTransferDialog, setShowTransferDialog] = useState(false);

  // DEBUG: Log image rating when modal opens
  useEffect(() => {
    if (isOpen && image) {
      console.log('[Modal] Image opened:', {
        id: image.id,
        user_rating: image.user_rating,
        average_rating: image.average_rating,
        rating_count: image.rating_count,
        has_user_rating_field: 'user_rating' in image,
      });
    }
  }, [isOpen, image]);

  // Keyboard Navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && onNavigatePrev) {
        e.preventDefault();
        onNavigatePrev();
      } else if (e.key === 'ArrowRight' && onNavigateNext) {
        e.preventDefault();
        onNavigateNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onNavigatePrev, onNavigateNext]);

  const loadTags = useCallback(async () => {
    if (!image?.id) return;

    setTagsLoading(true);
    try {
      const response = await getImageTags(image.id);
      if (response.success) {
        setTags(response.tags);
      }
    } catch (err) {
      console.error('Error loading tags:', err);
    } finally {
      setTagsLoading(false);
    }
  }, [image?.id]);

  // Tags laden wenn Modal öffnet
  useEffect(() => {
    if (isOpen && image?.id) {
      loadTags();
    }
  }, [isOpen, image?.id, loadTags]);

  // Tag erstellen
  const handleCreateTag = useCallback(async (name: string): Promise<Tag | null> => {
    try {
      const response = await createTag(name);
      return response.tag || null;
    } catch (err) {
      console.error('Error creating tag:', err);
      return null;
    }
  }, []);

  // Tags ändern (hinzufügen/entfernen)
  const handleTagsChange = useCallback(
    async (newTags: Tag[]) => {
      if (!image?.id) return;

      // Neue Tags finden (die noch nicht in der alten Liste sind)
      const addedTags = newTags.filter((t) => !tags.some((old) => old.id === t.id));
      // Entfernte Tags finden
      const removedTags = tags.filter((t) => !newTags.some((newT) => newT.id === t.id));

      // Optimistic Update
      setTags(newTags);

      try {
        // Neue Tags hinzufügen
        for (const tag of addedTags) {
          await addTagsToImage(image.id, [tag.name]);
        }

        // Entfernte Tags entfernen
        for (const tag of removedTags) {
          await removeTagFromImage(image.id, tag.id);
        }
      } catch (err) {
        console.error('Error updating tags:', err);
        // Bei Fehler: Tags neu laden
        await loadTags();
      }
    },
    [image?.id, tags, loadTags]
  );

  if (!image) return null;

  const imageUrl = `/image/${image.id}`;
  const createdAt = new Date(image.created_at);
  const formattedDate = createdAt.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const handleDownload = () => {
    downloadImage(image.id);
  };

  // Rating Handler
  const handleRatingChange = (rating: number) => {
    rateImage({ rating });
  };

  // Alternative erstellen: Neues Bild mit gleichen Settings generieren
  const handleAlternative = () => {
    toast.info(t('toast.info.alternativeCreating'), { duration: 2000 });
    generateVariation(image.id);
    onClose();
    // Navigation zur Einzelbild-Ansicht mit Generierungs-State
    navigate('/v2/tools/generate/single-v2?generating=true');
  };

  // Weiterverarbeiten: Bild + Settings in Editor laden
  const handleContinueEditing = () => {
    // URL-Parameter für Bild und Settings
    const params = new URLSearchParams({
      sourceImageId: image.id,
      ratio: image.ratio || '1:1',
      prompt: image.prompt || '',
    });
    if (image.template_id) {
      params.set('templateId', image.template_id);
    }

    onClose();
    navigate(`/v2/tools/generate/single-v2?${params.toString()}`);
  };

  const handlePublish = () => {
    if (window.confirm(t('gallery.publishImageConfirm'))) {
      publishImage(image.id);
    }
  };

  const handleDelete = () => {
    if (window.confirm(t('gallery.deleteImageConfirm'))) {
      deleteImage(image.id);
      onClose();
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/80 z-50" />
          <Dialog.Content
            className={cn(
              'fixed inset-4 md:inset-8 z-50',
              'bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden',
              'flex flex-col md:flex-row',
              'focus:outline-none'
            )}
          >
            {/* Close Button */}
            <Dialog.Close asChild>
              <button
                className={cn(
                  'absolute top-4 right-4 z-10',
                  'p-2 rounded-full',
                  'bg-black/50 hover:bg-black/70',
                  'text-white transition-colors'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>

            {/* Image Section */}
            <div className="flex-1 bg-neutral-950 flex items-center justify-center p-4 min-h-[300px] md:min-h-0 relative">
              {onNavigatePrev && (
                <button
                  onClick={onNavigatePrev}
                  className={cn('absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50')}
                  aria-label={t('gallery.prevImage')}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
              )}
              <img
                src={imageUrl}
                alt={image.prompt || 'Generiertes Bild'}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
              {onNavigateNext && (
                <button
                  onClick={onNavigateNext}
                  className={cn('absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50')}
                  aria-label={t('gallery.nextImage')}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              )}
            </div>

            {/* Sidebar */}
            <div className="w-full md:w-96 flex flex-col border-t md:border-t-0 md:border-l border-neutral-200 dark:border-neutral-700 overflow-y-auto">
              {/* Header */}
              <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-neutral-900 dark:text-white">
                  <span className="text-orange-500">
                    <Palette className="h-5 w-5" />
                  </span>
                  {t('common.details')}
                </h2>
              </div>

              {/* Content */}
              <div className="flex-1 px-6 py-4 space-y-4 overflow-y-auto">
                {/* Metadaten */}
                <div className="space-y-3">
                  <MetaItem icon={Calendar} label="Datum" value={formattedDate} />
                  <MetaItem icon={Code} label="Template" value={image.template_name || image.preset_type} />
                  <MetaItem icon={Maximize2} label={t('gallery.ratio')} value={image.ratio} />
                  {image.duration && (
                    <MetaItem icon={Clock} label="Dauer" value={`${image.duration}s`} />
                  )}
                  {/* Freigabestatus */}
                  {image.approval_status && (
                    <ApprovalStatusItem status={image.approval_status} imageId={image.id} />
                  )}
                </div>

                {/* Rating */}
                <div className="p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      Bewertung
                    </span>
                    {(image.average_rating ?? 0) > 0 && (
                      <span className="text-xs text-neutral-500 dark:text-neutral-400">
                        {(image.average_rating ?? 0).toFixed(1)} ({image.rating_count} {image.rating_count === 1 ? 'Bewertung' : 'Bewertungen'})
                      </span>
                    )}
                  </div>
                  <ImageRatingInput
                    value={image.user_rating ?? null}
                    onChange={handleRatingChange}
                    disabled={isRatingPending}
                    size="md"
                  />
                </div>

                {/* Prompt */}
                {image.prompt && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
                      <MessageSquare className="h-4 w-4" />
                      <span>Prompt</span>
                    </div>
                    <p className="text-sm text-neutral-700 dark:text-neutral-200 bg-neutral-50 dark:bg-neutral-800 p-3 rounded-lg">
                      {image.prompt}
                    </p>
                  </div>
                )}

                {/* AI Metadata */}
                <AIMetadataSection
                  metadata={image.ai_metadata || null}
                  fileName={image.file_name}
                  altTitle={image.alt_title}
                />

                {/* Tags */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
                    <TagIcon className="h-4 w-4" />
                    <span>{t('gallery.tags')}</span>
                    {tagsLoading && (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                    )}
                  </div>
                  <TagInput
                    tags={tags}
                    onChange={handleTagsChange}
                    onCreateTag={handleCreateTag}
                    placeholder={t('gallery.addTag')}
                    maxTags={40}
                  />
                </div>

                {/* Status */}
                {image.is_public_image && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400">
                    <Globe className="h-4 w-4" />
                    <span className="text-sm font-medium">{t('common.published')}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 space-y-2">
                <Button className="w-full" onClick={handleDownload} disabled={isDownloading}>
                  {isDownloading ? (
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  {t('common.download')}
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleAlternative}
                  disabled={isGeneratingVariation}
                >
                  {isGeneratingVariation ? (
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                  ) : (
                    <Dices className="h-4 w-4 mr-2" />
                  )}
                  Alternative erstellen
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleContinueEditing}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Weiterverarbeiten
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    // Open the new overlay AND close this Radix Dialog so its
                    // focus-trap releases — otherwise inputs in the overlay
                    // need an extra click on its header to gain focus.
                    setShowTransferDialog(true)
                    onClose()
                  }}
                >
                  <BoxSelect className="h-4 w-4 mr-2" />
                  {t('gallery.addToProduct')}
                </Button>

                {!image.is_public_image && (
                  <Button
                    variant="outline"
                    className="w-full text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950/30"
                    onClick={handlePublish}
                    disabled={isPublishing}
                  >
                    {isPublishing ? (
                      <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-green-500 border-t-transparent" />
                    ) : (
                      <Globe className="h-4 w-4 mr-2" />
                    )}
                    {t('common.publish')}
                  </Button>
                )}

                <Button
                  variant="outline"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Transfer Dialog (flag-gated: unified overlay vs. legacy dialog) */}
      <GalleryAddToProductBridge
        open={showTransferDialog}
        imageIds={image ? [image.id] : []}
        legacyImage={image}
        onClose={() => setShowTransferDialog(false)}
        onComplete={() => setShowTransferDialog(false)}
      />
    </>
  );
}

interface MetaItemProps {
  icon: React.ElementType;
  label: string;
  value: string;
}

function MetaItem({ icon: Icon, label, value }: MetaItemProps) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
      <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
        <Icon className="h-4 w-4" />
        <span>{label}</span>
      </div>
      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">{value}</span>
    </div>
  );
}

interface ApprovalStatusItemProps {
  status: 'pending' | 'approved' | 'rejected';
  imageId: string;
}

function ApprovalStatusItem({ status, imageId }: ApprovalStatusItemProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const statusConfig = {
    pending: {
      icon: AlertCircle,
      label: t('common.pending'),
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-950/30',
      clickable: true,
    },
    approved: {
      icon: CheckCircle,
      label: t('common.approve'),
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-950/30',
      clickable: false,
    },
    rejected: {
      icon: XCircle,
      label: t('common.reject'),
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-950/30',
      clickable: false,
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  const handleClick = () => {
    if (config.clickable) {
      // Navigate to review mode with pending filter and this image
      navigate(`/v2/tools/gallery/review?approval_status=pending&start_image=${imageId}`);
    }
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0">
      <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
        <Icon className="h-4 w-4" />
        <span>Freigabe</span>
      </div>
      {config.clickable ? (
        <button
          onClick={handleClick}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-md text-sm font-medium transition-colors',
            config.bgColor,
            config.color,
            'hover:opacity-80 cursor-pointer'
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {config.label}
        </button>
      ) : (
        <div
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-md text-sm font-medium',
            config.bgColor,
            config.color
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {config.label}
        </div>
      )}
    </div>
  );
}

export default ImageDetailModal;
